// Portions of this code are adapted from the MUI Toolbar component.
// Source: https://github.com/mui/toolpad/blob/master/packages/toolpad-core/src/DashboardLayout/DashboardLayout.tsx
// MUI is licensed under the MIT License: https://github.com/mui/toolpad/blob/master/LICENSE

import { ElementType, JSXElementConstructor } from "react";
import MuiAppBar from "@mui/material/AppBar";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import { styled } from "@mui/material/styles";
import { SidebarToggleButton } from "../Sidebar";
import AppTitle from "./components/AppTitle";
import { ComponentSlotProps, ComponentSlots } from "types/componentSlots";

const StyledAppBar = styled(MuiAppBar)(({ theme }) => ({
    borderWidth: 0,
    borderBottomWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.divider,
    boxShadow: 'none',
    zIndex: theme.zIndex.drawer + 1,
}));

type AppbarDefaultSlots = {
    appTitle: typeof AppTitle;
    actions: JSXElementConstructor<Record<string, unknown>>;
    accountMenu: JSXElementConstructor<Record<string, unknown>>;
};

type AppbarSlots = ComponentSlots<AppbarDefaultSlots>;

export interface AppbarProps<S extends AppbarSlots = AppbarSlots> {
    /**
     * Whether the navigation bar and menu icon should be hidden.
     * @default false
     */
    hideNavigation?: boolean;

    /**
     * Whether the sidebar should not be collapsible to a mini variant in desktop and tablet viewports.
     * @default false
     */
    disableCollapsibleSidebar?: boolean;

    slots?: Partial<S>;
    slotProps?: Partial<ComponentSlotProps<S, AppbarDefaultSlots>>;
}

export function Appbar<S extends AppbarSlots>({
    disableCollapsibleSidebar = false,
    hideNavigation = false,
    slots,
    slotProps
}: AppbarProps<S>) {

    // get the app title slot props
    const appTitleSlotProps = slotProps?.appTitle;

    let AppTitleSlot: ElementType = () => null;

    // only show the app title when the branding is not undefined
    if (appTitleSlotProps?.branding) {
        AppTitleSlot = slots?.appTitle ?? AppTitle;
    }


    const ActionsSlot = slots?.actions ?? (() => null);
    const AccountMenuSlot = slots?.accountMenu ?? (() => null);

    return (
        <StyledAppBar color="inherit" position="absolute" sx={{ displayPrint: 'none' }}>
            <Toolbar
                sx={{
                    backgroundColor: 'inherit',
                    // use narrower margin for mobile
                    mx: { xs: -0.75, sm: -1 },
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    flexWrap="nowrap"
                    flexGrow={1}
                    spacing={1}
                >
                    {/* menu icon and app title */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >

                        {!hideNavigation && (
                            <SidebarToggleButton disableCollapsibleSidebar={disableCollapsibleSidebar} />
                        )}

                        <AppTitleSlot {...appTitleSlotProps} />
                    </Stack>

                    {/* actions and account menu slot */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        flexGrow={1}
                        justifyContent="flex-end"
                        spacing={1}
                    >
                        <ActionsSlot {...slotProps?.actions} />
                        <AccountMenuSlot {...slotProps?.accountMenu} />
                    </Stack>

                </Stack>
            </Toolbar>
        </StyledAppBar>
    );
}

export default Appbar;